import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import {
  Space as SpaceType,
  SpaceQueryParams,
  PaginationInfo,
  Tag,
  SpaceStatus
} from '../../../types/space';
import spaceService from '../../../service/spaceService';
import tagService from '../../../service/tagService';
import { SUCCESS_MESSAGES, ERROR_MESSAGES, CONFIRM_MESSAGES } from '../configs/panelTableConfig';

export const useSpaceList = () => {
  const [spaces, setSpaces] = useState<SpaceType[]>([]);
  const [statistics, setStatistics] = useState<{
    total: number;
    buildings: number;
    zones: number;
    spaces: number;
    active: number;
    inactive: number;
  }>({
    total: 0,
    buildings: 0,
    zones: 0,
    spaces: 0,
    active: 0,
    inactive: 0
  });
  const [loading, setLoading] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingSpace, setEditingSpace] = useState<SpaceType | null>(null);
  const [selectedSpace, setSelectedSpace] = useState<SpaceType | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [queryParams, setQueryParams] = useState<SpaceQueryParams>({
    page: 1,
    pageSize: 10
  });
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // 加载标签数据
  const loadTags = useCallback(async () => {
    setTagsLoading(true);
    try {
      const response = await tagService.getTags({ page: 1, pageSize: 100 });
      if (response.success && response.data) {
        setTags(response.data || []);
      }
    } catch (error) {
      console.error('加载标签失败:', error);
    } finally {
      setTagsLoading(false);
    }
  }, []);

  // 加载空间数据 - 修复无限循环问题
  const loadSpaces = useCallback(async (params?: SpaceQueryParams) => {
    const currentParams = params || queryParams;
    console.log('查询参数:', currentParams);

    setLoading(true);
    try {
      const response = await spaceService.getSpaces(currentParams);
      console.log('API响应:', response);

      if (response.success && response.data) {
        setSpaces(response.data);
        // 更新分页信息
        if (response.pagination) {
          setPagination(response.pagination);
        }

      } else {
        message.error(response.error || ERROR_MESSAGES.LOAD);
        console.error('数据加载失败:', response.error);
      }
    } catch (error) {
      message.error(ERROR_MESSAGES.LOAD);
      console.error('数据加载异常:', error);
    } finally {
      setLoading(false);
    }

    // 同时更新统计数据
    loadStatistics(currentParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 移除 queryParams 依赖，通过参数传递

  // 加载统计数据
  const loadStatistics = useCallback(async (params?: SpaceQueryParams) => {
    try {
      // 只在有参数时才设置loading状态，避免初始化时闪烁
      if (params) {
        setStatisticsLoading(true);
      }
      const response = await spaceService.getSpaceStatistics(params);
      if (response.success && response.data) {
        setStatistics(response.data);
      } else {
        console.error('统计数据响应失败:', response.error);
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
    } finally {
      setStatisticsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSpaces();
    loadStatistics(); // 初始化时加载全局统计，不传递参数
    loadTags();
  }, [loadSpaces, loadStatistics, loadTags]);

  // 处理搜索
  const handleSearch = (values: any) => {
    console.log('搜索参数:', values);

    // 处理标签参数，确保空数组被正确处理
    const processedValues = { ...values };
    if (processedValues.tags === undefined || processedValues.tags === null) {
      delete processedValues.tags;
    } else if (Array.isArray(processedValues.tags) && processedValues.tags.length === 0) {
      delete processedValues.tags;
    }

    const newQueryParams = {
      ...queryParams,
      ...processedValues,
      page: 1
    };

    console.log('处理后的查询参数:', newQueryParams);
    setQueryParams(newQueryParams);
    loadSpaces(newQueryParams);
    loadStatistics(newQueryParams); // 同时更新统计数据
  };

  // 新建空间
  const handleCreate = () => {
    setEditingSpace(null); // 设置为null，确保表单清空
    setFormModalVisible(true);
  };

  // 添加子空间
  const handleAddChild = (parentSpace: SpaceType) => {
    // 只传递必要信息，避免传递整个父级空间对象导致表单被错误填充
    setEditingSpace({
      parentId: parentSpace.id,
      type: parentSpace.type, // 用于确定子空间类型
      // 不传递 id，这样 getDefaultFormValues 能正确识别为添加子空间
      // 不传递 name、tagIds、description 等字段，确保表单为空
    } as SpaceType);
    setFormModalVisible(true);
  };

  // 编辑空间
  const handleEdit = (space: SpaceType) => {
    setEditingSpace(space);
    setFormModalVisible(true);
  };

  // 查看详情
  const handleViewDetail = (space: SpaceType) => {
    setSelectedSpace(space);
    setDetailModalVisible(true);
  };

  // 删除空间
  const handleDelete = async (id: string) => {
    // 检查是否来自酒店，如果是则不允许删除
    const space = spaces.find(s => s.id === id);
    if (space?.fromHotel) {
      message.warning('来自酒店的空间不能删除');
      return;
    }
    
    try {
      const response = await spaceService.deleteSpace(id);
      if (response.success) {
        message.success(response.message || SUCCESS_MESSAGES.DELETE);
        loadSpaces();
      } else {
        message.error(response.error || ERROR_MESSAGES.DELETE);
      }
    } catch (error) {
      message.error(ERROR_MESSAGES.DELETE);
    }
  };

  // 批量删除
  const handleBatchDelete = async (ids: string[]) => {
    try {
      const response = await spaceService.batchDeleteSpaces(ids);
      if (response.success) {
        const successMessage = response.message ||
          CONFIRM_MESSAGES.BATCH_DELETE_SUCCESS.replace('{count}', response.data?.successful?.toString() || '0');
        message.success(successMessage);
        loadSpaces();
      } else {
        message.error(response.error || ERROR_MESSAGES.BATCH_DELETE);
      }
    } catch (error) {
      message.error(ERROR_MESSAGES.BATCH_DELETE);
    }
  };

  // 表单提交成功回调
  const handleFormSuccess = async (values: any): Promise<boolean> => {
    try {

      console.log('表单数据:', values);
      console.log('编辑中的空间:', editingSpace);

      let response;

      // 判断是否为编辑操作
      const isEdit = editingSpace && editingSpace.id;
      console.log('是否编辑模式:', isEdit);

      if (isEdit) {
        // 判断是否来自酒店
        const isFromHotel = editingSpace.fromHotel === true;
        
        // 编辑空间 - 如果来自酒店，只允许更新 tagIds
        const updateData = isFromHotel
          ? {
              tagIds: values.tagIds || [] // 来自酒店时，只更新标签
            }
          : {
              name: values.name,
              status: values.status,
              description: values.description || '', // 确保description是字符串
              tagIds: values.tagIds || [] // 包含标签ID列表
            };
        console.log('发送更新数据:', updateData);
        response = await spaceService.updateSpace(editingSpace.id, updateData);
      } else {
        // 新建空间（包括添加子空间）- 统一的数据结构
        const createData = {
          name: values.name,
          type: values.type,
          status: values.status,
          description: values.description || '', // 确保description是字符串
          parentId: values.parentId, // 新建根节点时为undefined，添加子空间时为父级ID
          tagIds: values.tagIds || [] // 包含标签ID列表
        };

        response = await spaceService.createSpace(createData);
      }

      console.log('API响应:', response);

      if (response.success) {
        message.success(response.message || (isEdit ? SUCCESS_MESSAGES.UPDATE : SUCCESS_MESSAGES.CREATE));
        setFormModalVisible(false);
        loadSpaces();
        return true;
      } else {
        console.error('操作失败:', response.error);
        message.error(response.error || (isEdit ? ERROR_MESSAGES.UPDATE : ERROR_MESSAGES.CREATE));
        return false;
      }
    } catch (error) {
      console.error('表单提交异常:', error);
      const isEdit = editingSpace && editingSpace.id;
      message.error(isEdit ? ERROR_MESSAGES.UPDATE : ERROR_MESSAGES.CREATE);
      return false;
    }
  };

  // 处理分页变化
  const handlePaginationChange = (pagination: any) => {
    console.log('分页变化:', pagination);
    const newQueryParams = {
      ...queryParams,
      page: pagination.current,
      pageSize: pagination.pageSize
    };
    setQueryParams(newQueryParams);
    loadSpaces(newQueryParams);
  };

  // 处理状态切换
  const handleStatusToggle = async (spaceId: string, currentStatus: SpaceStatus) => {
    try {
      const newStatus = currentStatus === SpaceStatus.ACTIVE ? SpaceStatus.INACTIVE : SpaceStatus.ACTIVE;

      const response = await spaceService.updateSpace(spaceId, { status: newStatus });

      if (response.success) {
        message.success(`状态已${newStatus === SpaceStatus.ACTIVE ? '启用' : '禁用'}`);

        // 更新本地状态
        setSpaces(prevSpaces =>
          prevSpaces.map(space =>
            space.id === spaceId
              ? { ...space, status: newStatus }
              : space
          )
        );

        // 重新加载统计数据
        loadStatistics(queryParams);
      } else {
        message.error(response.error || '状态更新失败');
      }
    } catch (error) {
      console.error('状态切换失败:', error);
      message.error('状态切换失败');
    }
  };

  // 关闭表单弹窗
  const closeFormModal = () => {
    setFormModalVisible(false);
  };

  // 关闭详情弹窗
  const closeDetailModal = () => {
    setDetailModalVisible(false);
  };

  return {
    // 状态
    spaces,
    statistics,
    loading,
    statisticsLoading,
    formModalVisible,
    detailModalVisible,
    editingSpace,
    selectedSpace,
    queryParams,
    pagination,
    tags,
    tagsLoading,

    // 方法
    loadSpaces,
    loadStatistics,
    handleSearch,
    handleCreate,
    handleAddChild,
    handleEdit,
    handleViewDetail,
    handleDelete,
    handleBatchDelete,
    handleStatusToggle,
    handleFormSuccess,
    handlePaginationChange,
    closeFormModal,
    closeDetailModal
  };
}; 